import { ShieldCheck, Clock, AlertTriangle, ExternalLink, Zap } from "lucide-react";

import { _t } from "@/lib/i18n";
// Human-readable reasons per backend status code
const REASON_LABEL = {
  config_not_ready: _t("Contratto non ancora deployato — riproveremo automaticamente."),
  rpc_unreachable: _t("RPC Polygon Amoy non raggiungibile. Nuovo tentativo in corso."),
  awaiting_receipt: _t("Transazione inviata, in attesa della conferma on-chain."),
  gas_estimate_failed: _t("Stima del gas fallita. Riprova automatica."),
  tx_reverted: _t("La transazione è stata rifiutata dalla rete. Reintervento richiesto."),
  hash_mismatch: _t("L'hash on-chain non combacia. Anchor da rifare."),
  invalid_unlock_at: _t("Data di apertura non valida. Contatta il supporto."),
};

const AMOY_EXPLORER = "https://amoy.polygonscan.com";

/**
 * Compact blockchain status badge for capsules.
 * Reads `blockchain_status` (+ optional tx_hash, block_number, contract_address)
 * from the API and renders a color-coded status:
 *   - PENDING    → amber pulsing "Ancoraggio in corso"
 *   - CONFIRMED  → green "Sigillato su Polygon Amoy" + explorer link
 *   - SIMULATED  → gray "Sigillo simulato (dev)"
 *   - null       → nothing (no anchor requested yet)
 *
 * Sized to sit inside the sealed/traveling/delivered views alongside AnchorPanel.
 */
export default function BlockchainStatusBadge({ cap, className = "" }) {
  if (!cap) return null;
  const status = cap.blockchain_status;
  if (!status) return null;

  if (status === "CONFIRMED") {
    const txHash = cap.blockchain_tx_hash;
    const explorerUrl = txHash ? `${AMOY_EXPLORER}/tx/${txHash}` : null;
    return (
      <div
        data-testid="blockchain-badge-confirmed"
        className={`tc-card p-4 ${className}`}
        style={{ borderColor: _t("rgba(16,185,129,0.35)"), background: _t("rgba(16,185,129,0.06)") }}
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="mt-0.5">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-1">{_t("Sigillato on-chain")}</div>
            <div className="text-white text-sm">{_t("Ancoraggio confermato su")}{" "}<span className="font-medium">{_t("Polygon Amoy")}</span>
            </div>
            {cap.blockchain_block_number != null && (
              <div className="text-xs text-white/50 mt-1">{_t("Block #{0} {1}", cap.blockchain_block_number, txHash && (
                  <>
                    {" · "}
                    <span className="font-mono-launch text-white/70">
                      {txHash.slice(0, 10)}…{txHash.slice(-6)}
                    </span>
                  </>
                ))}</div>
            )}
          </div>
          {explorerUrl && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="blockchain-badge-explorer-link"
              className="btn-ghost !text-xs !px-3"
            >
              <ExternalLink className="w-3.5 h-3.5" />{" "}{_t("Explorer")}</a>
          )}
        </div>
      </div>
    );
  }

  if (status === "PENDING") {
    const reason = cap.blockchain_pending_reason || null;
    const reasonLabel = reason ? REASON_LABEL[reason] || reason : null;
    return (
      <div
        data-testid="blockchain-badge-pending"
        className={`tc-card p-4 ${className}`}
        style={{ borderColor: _t("rgba(245,158,11,0.35)"), background: _t("rgba(245,158,11,0.06)") }}
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="mt-0.5 relative">
            <Clock className="w-5 h-5 text-amber-400" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 animate-ping" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-1">{_t("Ancoraggio in corso")}</div>
            <div className="text-white text-sm">{_t("La capsula è cifrata e sigillata localmente. L'ancoraggio su Polygon Amoy verrà completato appena la rete conferma la transazione.")}</div>
            {reasonLabel && (
              <div className="text-xs text-white/50 mt-1" title={reason}>
                {reasonLabel}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (status === "SIMULATED") {
    return (
      <div
        data-testid="blockchain-badge-simulated"
        className={`tc-card p-4 ${className}`}
        style={{ borderColor: _t("rgba(148,163,184,0.35)"), background: _t("rgba(148,163,184,0.06)") }}
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="mt-0.5">
            <Zap className="w-5 h-5 text-slate-300" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-300 mb-1">{_t("Sigillo simulato")}</div>
            <div className="text-white/80 text-sm">{_t("Ambiente di sviluppo — nessuna transazione reale on-chain.")}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="blockchain-badge-unknown"
      className={`tc-card p-4 ${className}`}
      style={{ borderColor: _t("rgba(255,255,255,0.15)") }}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-white/50" />
        <div className="text-sm text-white/60">{_t("Stato ancoraggio sconosciuto: {0}", String(status))}</div>
      </div>
    </div>
  );
}
